import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  UserCircle, Eye, EyeOff, Settings, Palette, Type, Bell, 
  Archive, Globe, Moon, Sun, Monitor, Check, ChevronRight,
  User, Lock, Trash2, Wand2, Zap, Sparkles, Brain
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/i18n";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Tema seçenekleri
const THEMES = [
  { id: "dark", labelKey: "theme_dark", icon: Moon },
  { id: "light", labelKey: "theme_light", icon: Sun },
  { id: "system", labelKey: "theme_system", icon: Monitor }
];

// Yazı tipi boyutları
const FONT_SIZES = [
  { id: "small", labelKey: "font_small", size: "14px" },
  { id: "medium", labelKey: "font_medium", size: "16px" },
  { id: "large", labelKey: "font_large", size: "18px" }
];

// Dil seçenekleri
const LANGUAGES = [
  { id: "tr", label: "Türkçe", flag: "🇹🇷" },
  { id: "en", label: "English", flag: "🇬🇧" },
  { id: "de", label: "Deutsch", flag: "🇩🇪" },
  { id: "fr", label: "Français", flag: "🇫🇷" },
  { id: "es", label: "Español", flag: "🇪🇸" },
  { id: "ar", label: "العربية", flag: "🇸🇦" },
  { id: "ru", label: "Русский", flag: "🇷🇺" },
  { id: "zh", label: "中文", flag: "🇨🇳" },
  { id: "ja", label: "日本語", flag: "🇯🇵" }
];

// Ayar sekmeleri
const SETTINGS_TABS = [
  { id: "profile", labelKey: "profile", icon: User },
  { id: "appearance", labelKey: "appearance", icon: Palette },
  { id: "notifications", labelKey: "notifications", icon: Bell },
  { id: "archive", labelKey: "archive", icon: Archive },
  { id: "language", labelKey: "language", icon: Globe },
  { id: "memory", labelKey: "memory_tab", icon: Brain },
  { id: "security", labelKey: "security", icon: Lock }
];

export const ProfileModal = ({ 
  open, 
  onOpenChange, 
  user,
  onChangePassword,
  onUpdateProfile,
  archivedConversations = [],
  onUnarchiveConversation,
  onDeleteArchivedConversation,
  onThemeChange,
  onRequestUpgrade
}) => {
  const { language: currentLang, setLanguage: setGlobalLanguage, t } = useLanguage();
  const [activeTab, setActiveTab] = useState("profile");

  // Digital Partner memory state
  const [memoryData, setMemoryData] = useState(null);
  const [memoryLoading, setMemoryLoading] = useState(false);

  const fetchMemory = async () => {
    setMemoryLoading(true);
    try {
      const res = await axios.get(`${API}/memory/partner`);
      setMemoryData(res.data);
    } catch (e) {
      console.warn("memory fetch failed", e);
    } finally {
      setMemoryLoading(false);
    }
  };

  useEffect(() => {
    if (open && activeTab === "memory") fetchMemory();
  }, [open, activeTab]);

  const toggleMemory = async (enabled) => {
    setMemoryData((p) => ({ ...(p || {}), enabled }));
    try {
      await axios.put(`${API}/memory/partner/toggle`, { enabled });
    } catch (e) {
      toast.error(t('memory_update_failed'));
      setMemoryData((p) => ({ ...(p || {}), enabled: !enabled }));
    }
  };

  const deleteMemoryFact = async (id) => {
    try {
      const res = await axios.delete(`${API}/memory/partner/fact/${id}`);
      setMemoryData((p) => ({ ...(p || {}), auto_facts: res.data.auto_facts }));
    } catch (e) {
      toast.error(t('memory_update_failed'));
    }
  };

  const clearMemory = async () => {
    try {
      await axios.delete(`${API}/memory/partner`);
      setMemoryData((p) => ({ ...(p || {}), auto_facts: [], mood: "" }));
      toast.success(t('memory_cleared'));
    } catch (e) {
      toast.error(t('memory_update_failed'));
    }
  };

  const memoryCatLabel = (c) => t(`memory_cat_${c}`);
  
  // Profile state
  const [name, setName] = useState(user?.name || "");
  const [isEditingName, setIsEditingName] = useState(false);
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Settings state (localStorage)
  const [settings, setSettings] = useState({
    theme: "dark",
    fontSize: "medium",
    language: currentLang,
    autoCorrect: false,
    modelMode: "auto",
    notifications: {
      sound: true,
      desktop: false,
      email: true
    }
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem("kaan-ai-settings");
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        // Use language from context (which is already synced with localStorage)
        setSettings({ ...parsed, language: currentLang });
        // Apply saved settings immediately
        applyTheme(parsed.theme);
        applyFontSize(parsed.fontSize);
      } catch (e) {
        console.error("Failed to load settings:", e);
      }
    }
  }, [currentLang]);

  // Apply theme function
  const applyTheme = (theme) => {
    const root = document.documentElement;
    const body = document.body;
    
    let effectiveTheme = theme;
    if (theme === "system") {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      effectiveTheme = prefersDark ? "dark" : "light";
    }
    
    if (effectiveTheme === "light") {
      root.classList.add("light-theme");
      body.classList.add("light-theme");
      // Force CSS variable changes
      root.style.setProperty('--bg-primary', '#ffffff');
      root.style.setProperty('--bg-secondary', '#f8fafc');
      root.style.setProperty('--bg-tertiary', '#f1f5f9');
      root.style.setProperty('--bg-hover', '#e2e8f0');
      root.style.setProperty('--text-primary', '#0f172a');
      root.style.setProperty('--text-secondary', '#475569');
      root.style.setProperty('--text-muted', '#94a3b8');
      root.style.setProperty('--border-color', '#e2e8f0');
      root.style.setProperty('--card-bg', '#ffffff');
      root.style.setProperty('--input-bg', '#f1f5f9');
      root.style.setProperty('--sidebar-bg', '#f8fafc');
      // Tailwind CSS variables
      root.style.setProperty('--background', '0 0% 100%');
      root.style.setProperty('--foreground', '222 47% 11%');
      root.style.setProperty('--card', '0 0% 100%');
      root.style.setProperty('--card-foreground', '222 47% 11%');
      root.style.setProperty('--border', '214 32% 91%');
      root.style.setProperty('--input', '214 32% 91%');
    } else {
      root.classList.remove("light-theme");
      body.classList.remove("light-theme");
      // Reset to dark CSS variables
      root.style.setProperty('--bg-primary', '#09090b');
      root.style.setProperty('--bg-secondary', '#18181b');
      root.style.setProperty('--bg-tertiary', '#27272a');
      root.style.setProperty('--bg-hover', '#3f3f46');
      root.style.setProperty('--text-primary', '#fafafa');
      root.style.setProperty('--text-secondary', '#a1a1aa');
      root.style.setProperty('--text-muted', '#71717a');
      root.style.setProperty('--border-color', '#27272a');
      root.style.setProperty('--card-bg', '#18181b');
      root.style.setProperty('--input-bg', '#27272a');
      root.style.setProperty('--sidebar-bg', '#0a0a0b');
      // Tailwind CSS variables
      root.style.setProperty('--background', '240 10% 3.9%');
      root.style.setProperty('--foreground', '0 0% 98%');
      root.style.setProperty('--card', '240 10% 3.9%');
      root.style.setProperty('--card-foreground', '0 0% 98%');
      root.style.setProperty('--border', '240 5% 17%');
      root.style.setProperty('--input', '240 5% 17%');
    }
    
    // Update parent component state directly
    if (onThemeChange) {
      onThemeChange(effectiveTheme);
    }
    
    // Dispatch custom event as backup
    window.dispatchEvent(new CustomEvent('themeChange', { detail: effectiveTheme }));
  };

  // Apply font size function
  const applyFontSize = (size) => {
    const fontSize = FONT_SIZES.find(f => f.id === size)?.size || "16px";
    document.documentElement.style.fontSize = fontSize;
    document.body.style.fontSize = fontSize;
  };

  // Save settings to localStorage (no toast)
  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem("kaan-ai-settings", JSON.stringify(newSettings));
    
    // Apply changes immediately
    if (newSettings.theme !== settings.theme) {
      applyTheme(newSettings.theme);
    }
    if (newSettings.fontSize !== settings.fontSize) {
      applyFontSize(newSettings.fontSize);
    }
    // Apply language change immediately
    if (newSettings.language !== settings.language) {
      setGlobalLanguage(newSettings.language);
    }
  };

  // Update name (no toast)
  const updateName = async () => {
    if (name.trim() && name !== user?.name) {
      try {
        await axios.put(`${API}/auth/profile`, { name: name.trim() });
        if (onUpdateProfile) {
          onUpdateProfile({ ...user, name: name.trim() });
        }
        setIsEditingName(false);
      } catch (error) {
        // Silently fail or just log
        console.error("Failed to update name:", error);
      }
    } else {
      setIsEditingName(false);
    }
  };

  const handleChangePassword = async () => {
    const result = await onChangePassword(currentPassword, newPassword, confirmPassword);
    if (result) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  // Reset name when user changes
  useEffect(() => {
    setName(user?.name || "");
  }, [user]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-secondary)] border-[var(--border-color)] max-w-2xl max-h-[85vh] overflow-hidden p-0">
        <div className="flex h-[70vh]">
          {/* Sidebar */}
          <div className="w-48 bg-[var(--bg-primary)] border-r border-[var(--border-color)] p-3">
            <DialogHeader className="pb-4">
              <DialogTitle className="text-[var(--text-primary)] flex items-center gap-2 text-base">
                <Settings size={18} /> {t('settings')}
              </DialogTitle>
            </DialogHeader>
            
            <nav className="space-y-1">
              {SETTINGS_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                    activeTab === tab.id
                      ? "bg-blue-600 text-white"
                      : "text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
                  }`}
                >
                  <tab.icon size={16} />
                  {t(tab.labelKey)}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {/* Profile Tab */}
            {activeTab === "profile" && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <User size={20} /> {t('profile_info')}
                </h2>
                
                <div className="space-y-4">
                  {/* Email */}
                  <div className="bg-[var(--bg-tertiary)]/50 p-4 rounded-lg">
                    <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-1">{t('email')}</p>
                    <p className="text-[var(--text-primary)]">{user?.email}</p>
                  </div>
                  
                  {/* Name */}
                  <div className="bg-[var(--bg-tertiary)]/50 p-4 rounded-lg">
                    <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-2">{t('name')}</p>
                    {isEditingName ? (
                      <div className="flex gap-2">
                        <Input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="bg-[var(--input-bg)] border-[var(--border-color)] flex-1 text-[var(--text-primary)]"
                          placeholder={t('name')}
                          autoFocus
                          onKeyPress={(e) => e.key === "Enter" && updateName()}
                        />
                        <Button onClick={updateName} size="sm" className="bg-blue-600">
                          <Check size={16} />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <p className="text-[var(--text-primary)]">{user?.name}</p>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setIsEditingName(true)}
                          className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        >
                          {t('edit')}
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Account Type */}
                  <div className="bg-[var(--bg-tertiary)]/50 p-4 rounded-lg">
                    <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-1">{t('account_type')}</p>
                    <p className="text-[var(--text-primary)]">
                      {user?.is_admin ? t('admin') : user?.subscription?.plan_name || t('free')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Appearance Tab */}
            {activeTab === "appearance" && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <Palette size={20} /> {t('appearance')}
                </h2>
                
                {/* Theme */}
                <div className="space-y-3">
                  <Label className="text-[var(--text-secondary)]">{t('theme')}</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {THEMES.map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => saveSettings({ ...settings, theme: theme.id })}
                        className={`p-4 rounded-lg border transition-all flex flex-col items-center gap-2 ${
                          settings.theme === theme.id
                            ? "border-blue-500 bg-blue-500/20"
                            : "border-[var(--border-color)] bg-[var(--bg-tertiary)]/50 hover:bg-[var(--bg-hover)]"
                        }`}
                      >
                        <theme.icon size={24} className={settings.theme === theme.id ? "text-blue-400" : "text-[var(--text-secondary)]"} />
                        <span className="text-sm text-[var(--text-primary)]">{t(theme.labelKey)}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Font Size */}
                <div className="space-y-3">
                  <Label className="text-[var(--text-secondary)]">{t('font_size')}</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {FONT_SIZES.map((font) => (
                      <button
                        key={font.id}
                        onClick={() => saveSettings({ ...settings, fontSize: font.id })}
                        className={`p-4 rounded-lg border transition-all ${
                          settings.fontSize === font.id
                            ? "border-blue-500 bg-blue-500/20"
                            : "border-[var(--border-color)] bg-[var(--bg-tertiary)]/50 hover:bg-[var(--bg-hover)]"
                        }`}
                      >
                        <span className="text-[var(--text-primary)]" style={{ fontSize: font.size }}>{t(font.labelKey)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Auto Correct */}
                <div className="space-y-3">
                  <Label className="text-[var(--text-secondary)]">{t('auto_correct')}</Label>
                  <div className="flex items-center justify-between p-4 bg-[var(--bg-tertiary)]/50 rounded-lg border border-[var(--border-color)]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <Wand2 size={20} className="text-purple-400" />
                      </div>
                      <div>
                        <p className="text-[var(--text-primary)] font-medium">{t('auto_correct')}</p>
                        <p className="text-sm text-[var(--text-muted)]">{t('auto_correct_desc')}</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.autoCorrect}
                      onCheckedChange={(checked) => saveSettings({ ...settings, autoCorrect: checked })}
                    />
                  </div>
                </div>

                {/* AI Model Mode */}
                <div className="space-y-3" data-testid="model-mode-section">
                  <Label className="text-[var(--text-secondary)]">{t('ai_model_mode')}</Label>
                  <p className="text-xs text-[var(--text-muted)]">{t('ai_model_mode_desc')}</p>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: "fast", icon: Zap, color: "yellow", labelKey: "model_fast", descKey: "model_fast_desc", premium: false },
                      { id: "auto", icon: Sparkles, color: "blue", labelKey: "model_auto", descKey: "model_auto_desc", premium: false },
                      { id: "pro", icon: Brain, color: "purple", labelKey: "model_pro", descKey: "model_pro_desc", premium: true }
                    ].map((mode) => {
                      const Icon = mode.icon;
                      const isSelected = settings.modelMode === mode.id;
                      const isPremiumUser = user?.is_admin || (user?.subscription && user.subscription.plan_name);
                      const isLocked = mode.premium && !isPremiumUser;
                      
                      return (
                        <button
                          key={mode.id}
                          data-testid={`model-mode-${mode.id}`}
                          onClick={() => {
                            if (isLocked) {
                              if (onRequestUpgrade) {
                                onRequestUpgrade();
                              } else {
                                toast.info(t('pro_mode_locked'), { duration: 3000 });
                              }
                              return;
                            }
                            saveSettings({ ...settings, modelMode: mode.id });
                          }}
                          className={`relative p-4 rounded-lg border transition-all flex flex-col items-center gap-2 text-center ${
                            isSelected
                              ? `border-${mode.color}-500 bg-${mode.color}-500/20`
                              : "border-[var(--border-color)] bg-[var(--bg-tertiary)]/50 hover:bg-[var(--bg-hover)]"
                          } ${isLocked ? "opacity-60" : ""}`}
                        >
                          {isLocked && (
                            <span className="absolute top-1 right-1 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-300 font-medium">
                              {t('premium')}
                            </span>
                          )}
                          <Icon size={24} className={isSelected ? `text-${mode.color}-400` : "text-[var(--text-secondary)]"} />
                          <span className="text-sm font-medium text-[var(--text-primary)]">{t(mode.labelKey)}</span>
                          <span className="text-[10px] text-[var(--text-muted)] leading-tight">{t(mode.descKey)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === "notifications" && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <Bell size={20} /> {t('notification_settings')}
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-[var(--bg-tertiary)]/50 rounded-lg">
                    <div>
                      <p className="text-[var(--text-primary)] font-medium">{t('sound_notifications')}</p>
                      <p className="text-sm text-[var(--text-muted)]">{t('sound_notifications_desc')}</p>
                    </div>
                    <Switch
                      checked={settings.notifications?.sound}
                      onCheckedChange={(checked) => saveSettings({
                        ...settings,
                        notifications: { ...settings.notifications, sound: checked }
                      })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-[var(--bg-tertiary)]/50 rounded-lg">
                    <div>
                      <p className="text-[var(--text-primary)] font-medium">{t('desktop_notifications')}</p>
                      <p className="text-sm text-[var(--text-muted)]">{t('desktop_notifications_desc')}</p>
                    </div>
                    <Switch
                      checked={settings.notifications?.desktop}
                      onCheckedChange={(checked) => {
                        if (checked && "Notification" in window) {
                          Notification.requestPermission();
                        }
                        saveSettings({
                          ...settings,
                          notifications: { ...settings.notifications, desktop: checked }
                        });
                      }}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-[var(--bg-tertiary)]/50 rounded-lg">
                    <div>
                      <p className="text-[var(--text-primary)] font-medium">{t('email_notifications')}</p>
                      <p className="text-sm text-[var(--text-muted)]">{t('email_notifications_desc')}</p>
                    </div>
                    <Switch
                      checked={settings.notifications?.email}
                      onCheckedChange={(checked) => saveSettings({
                        ...settings,
                        notifications: { ...settings.notifications, email: checked }
                      })}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Archive Tab */}
            {activeTab === "archive" && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <Archive size={20} /> {t('archived_chats')}
                </h2>
                
                {archivedConversations.length === 0 ? (
                  <div className="text-center py-12 text-[var(--text-muted)]">
                    <Archive size={48} className="mx-auto mb-3 opacity-50" />
                    <p>{t('no_archived')}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {archivedConversations.map((conv) => (
                      <div key={conv.id} className="flex items-center justify-between p-3 bg-[var(--bg-tertiary)]/50 rounded-lg">
                        <div className="flex-1">
                          <p className="text-[var(--text-primary)] text-sm">{conv.title}</p>
                          <p className="text-xs text-[var(--text-muted)]">
                            {new Date(conv.updated_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onUnarchiveConversation && onUnarchiveConversation(conv.id)}
                            className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                          >
                            <Archive size={14} className="mr-1" />
                            {t('unarchive')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteArchivedConversation && onDeleteArchivedConversation(conv.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Language Tab */}
            {activeTab === "language" && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <Globe size={20} /> {t('language_settings')}
                </h2>
                
                <div className="space-y-3">
                  <Label className="text-[var(--text-secondary)]">{t('app_language')}</Label>
                  <div className="space-y-2">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.id}
                        onClick={() => saveSettings({ ...settings, language: lang.id })}
                        className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all ${
                          settings.language === lang.id
                            ? "border-blue-500 bg-blue-500/20"
                            : "border-[var(--border-color)] bg-[var(--bg-tertiary)]/50 hover:bg-[var(--bg-hover)]"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{lang.flag}</span>
                          <span className="text-[var(--text-primary)]">{lang.label}</span>
                        </div>
                        {settings.language === lang.id && (
                          <Check size={20} className="text-blue-400" />
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mt-2">
                    {t('language_note')}
                  </p>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === "security" && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <Lock size={20} /> {t('security')}
                </h2>
                
                <div className="space-y-4">
                  <div className="bg-[var(--bg-tertiary)]/50 p-4 rounded-lg">
                    <p className="text-sm text-[var(--text-secondary)] mb-4">{t('change_password')}</p>
                    <div className="space-y-3">
                      <div className="relative">
                        <Input 
                          type={showCurrentPassword ? "text" : "password"}
                          placeholder={t('current_password')}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="bg-[var(--input-bg)] border-[var(--border-color)] text-[var(--text-primary)] pr-10"
                        />
                        <button 
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        >
                          {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <div className="relative">
                        <Input 
                          type={showNewPassword ? "text" : "password"}
                          placeholder={t('new_password')}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="bg-[var(--input-bg)] border-[var(--border-color)] text-[var(--text-primary)] pr-10"
                        />
                        <button 
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        >
                          {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <Input 
                        type="password"
                        placeholder={t('confirm_password')}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="bg-[var(--input-bg)] border-[var(--border-color)] text-[var(--text-primary)]"
                      />
                      <Button 
                        onClick={handleChangePassword} 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        {t('change_password')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Digital Partner Memory Tab */}
            {activeTab === "memory" && (
              <div className="space-y-5" data-testid="memory-tab-panel">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <Brain size={20} /> {t('memory_title')}
                </h2>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{t('memory_desc')}</p>

                <div className="flex items-center justify-between bg-[var(--bg-tertiary)]/50 p-4 rounded-lg">
                  <Label className="text-sm text-[var(--text-primary)]">{t('memory_enabled_label')}</Label>
                  <Switch
                    checked={memoryData?.enabled !== false}
                    onCheckedChange={toggleMemory}
                    data-testid="memory-toggle"
                  />
                </div>

                {memoryData?.mood && (
                  <div className="text-sm text-[var(--text-secondary)]">
                    {t('memory_mood_label')}: <span className="text-indigo-400 font-medium">{memoryData.mood}</span>
                  </div>
                )}

                {memoryLoading ? (
                  <p className="text-sm text-[var(--text-muted)]">...</p>
                ) : memoryData?.auto_facts?.length ? (
                  <div className="space-y-2">
                    {memoryData.auto_facts.map((f) => (
                      <div
                        key={f.id}
                        className="flex items-center justify-between gap-3 bg-[var(--bg-tertiary)]/40 px-3 py-2 rounded-lg"
                        data-testid="memory-fact"
                      >
                        <div className="min-w-0">
                          <span className="text-[10px] uppercase tracking-wider text-indigo-400/80">{memoryCatLabel(f.category)}</span>
                          <p className="text-sm text-[var(--text-primary)] break-words">{f.text}</p>
                        </div>
                        <button
                          onClick={() => deleteMemoryFact(f.id)}
                          data-testid="memory-fact-delete"
                          className="text-[var(--text-muted)] hover:text-red-400 transition-colors shrink-0"
                          title={t('delete')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--text-muted)]" data-testid="memory-empty">{t('memory_empty')}</p>
                )}

                {memoryData?.auto_facts?.length > 0 && (
                  <Button
                    onClick={clearMemory}
                    variant="outline"
                    className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    data-testid="memory-clear-all"
                  >
                    <Trash2 size={16} className="mr-1" /> {t('memory_clear_all')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
